import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Upload, X, AlertCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createComplaint, uploadComplaintFile, ComplaintData } from '../../services/complaintsApi';

interface ComplaintFormProps {
  onBack?: () => void;
  onSubmit?: () => void;
}


const categories = [
  'Technical Issue',
  'Account Problem', 
  'Payment Issue',
  'General Inquiry',
  'Bug Report',
  'Feature Request',
  'Other'
];

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

export function ComplaintForm({ onBack, onSubmit }: ComplaintFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ComplaintData>({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    screenshots: [],
    attachments: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ComplaintData>>({});

  const handleInputChange = (field: keyof ComplaintData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = async (file: File, type: 'screenshots' | 'attachments') => {
    try {
      const folderPath = 'complaints/attachments';
      toast.loading(`Uploading ${file.name}...`, { id: `upload-${file.name}` });
      
      const response = await uploadComplaintFile(file, folderPath);
      
      if (response.success) {
        const fileUrl = response.data.url;
        setFormData(prev => ({
          ...prev,
          [type]: [...(prev[type] || []), fileUrl]
        }));
        toast.success(`${file.name} uploaded successfully!`, { id: `upload-${file.name}` });
      }
    } catch (error: any) {
      console.error('File upload error:', error);
      toast.error(`Failed to upload ${file.name}: ${error.message || 'Unknown error'}`, { id: `upload-${file.name}` });
    }
  };

  const removeFile = (index: number, type: 'screenshots' | 'attachments') => {
    setFormData(prev => ({
      ...prev,
      [type]: (prev[type] || []).filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ComplaintData> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createComplaint(formData);

      if (response.success) {
        toast.success('Complaint submitted successfully!');
        if (onSubmit) {
          onSubmit();
        } else {
          navigate('/complaints');
        }
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit complaint');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Submit a Complaint</h1>
                <p className="text-sm text-gray-500">Create a new complaint ticket</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-semibold text-gray-900">Complaint Details</CardTitle>
            <CardDescription className="text-gray-600">
              Please provide detailed information about your issue. We'll review and respond as soon as possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                  <p className="text-sm text-gray-500 mt-1">Provide the essential details about your complaint</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title */}
                  <div className="md:col-span-2">
                    <Label htmlFor="title" className="text-sm font-medium text-gray-900">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Brief description of your issue"
                      className={`mt-2 ${errors.title ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <Label htmlFor="category" className="text-sm font-medium text-gray-900">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => handleInputChange('category', value)}
                    >
                      <SelectTrigger className={`mt-2 ${errors.category ? 'border-red-500' : ''}`}>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.category}
                      </p>
                    )}
                  </div>

                  {/* Priority */}
                  <div>
                    <Label htmlFor="priority" className="text-sm font-medium text-gray-900">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => handleInputChange('priority', value)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map((priority) => (
                          <SelectItem key={priority.value} value={priority.value}>
                            {priority.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-gray-900">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Please provide detailed information about your issue..."
                    rows={6}
                    className={`mt-2 ${errors.description ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </p>
                  )}
                </div>
              </div>

              {/* File Attachments */}
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium text-gray-900">File Attachments</h3>
                  <p className="text-sm text-gray-500 mt-1">Add screenshots or documents to support your complaint</p>
                </div>

                {/* Screenshots */}
                <div>
                  <Label className="text-sm font-medium text-gray-900">Screenshots</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(file => handleFileUpload(file, 'screenshots'));
                      }}
                      className="hidden"
                      id="screenshot-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('screenshot-upload')?.click()}
                      className="mb-4 hover:bg-gray-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Screenshots
                    </Button>
                    
                    {(formData.screenshots?.length || 0) > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {(formData.screenshots || []).map((url, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                              <img
                                src={url}
                                alt={`Screenshot ${index + 1}`}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                onLoad={() => console.log('Image loaded:', url)}
                                onError={(e) => {
                                  console.error('Image failed to load:', url);
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (fallback) {
                                    fallback.classList.remove('hidden');
                                  }
                                }}
                              />
                              <div className="hidden w-full h-full flex items-center justify-center text-gray-500 bg-gray-50">
                                <div className="text-center">
                                  <FileText className="w-8 h-8 mx-auto mb-2" />
                                  <span className="text-sm">Preview not available</span>
                                  <div className="text-xs mt-1 break-all px-2">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeFile(index, 'screenshots')}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                            <div className="mt-2 text-xs text-gray-500 text-center">
                              Screenshot {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <Label className="text-sm font-medium text-gray-900">Attachments</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(file => handleFileUpload(file, 'attachments'));
                      }}
                      className="hidden"
                      id="attachment-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('attachment-upload')?.click()}
                      className="mb-4 hover:bg-gray-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Files
                    </Button>
                    
                    {(formData.attachments?.length || 0) > 0 && (
                      <div className="space-y-3">
                        {(formData.attachments || []).map((url, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors group">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  Attachment {index + 1}
                                </div>
                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                  {url.split('/').pop()}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(url, '_blank')}
                                className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-50 hover:border-blue-300"
                              >
                                View
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => removeFile(index, 'attachments')}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-600 hover:bg-orange-700 px-6"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
